import { editableValue, sameFieldValue } from "./editableValue";

const valuesById = (snapshot) =>
  Object.fromEntries(
    (snapshot.values || []).map((row) => [row.definition_id, row.value]),
  );

/** Recover only user changes after an explicit reload, using the latest authority. */
const recoverAdditionalDetailsDraft = (snapshot, previousDraft, current) => {
  const original = valuesById(snapshot);
  const saved = valuesById(current);
  const priorDefinitions = new Map(
    snapshot.definitions.map((field) => [String(field.id), field]),
  );
  const currentDefinitions = new Map(
    current.definitions.map((field) => [String(field.id), field]),
  );
  const draft = {};
  const conflicts = {};
  let removedChoices = false;
  let changedType = false;

  for (const field of current.definitions.filter((item) => item.editable)) {
    const previous = priorDefinitions.get(String(field.id));
    draft[field.id] = editableValue(field, saved[field.id] ?? null);
    if (!previous?.editable || !Object.hasOwn(previousDraft, field.id)) {
      continue;
    }
    if (previous.field_type !== field.field_type) {
      changedType = true;
      continue;
    }
    const originalEditable = editableValue(previous, original[field.id]);
    if (sameFieldValue(field, previousDraft[field.id], originalEditable)) {
      continue;
    }
    const value = editableValue(field, previousDraft[field.id]);
    removedChoices ||= !sameFieldValue(field, value, previousDraft[field.id]);
    draft[field.id] = value;
    if (
      !sameFieldValue(
        field,
        editableValue(field, saved[field.id]),
        // Compare with what the user could originally edit. A restored saved
        // choice is newly available and must be reviewed against their draft.
        editableValue(field, originalEditable),
      ) &&
      !sameFieldValue(field, editableValue(field, saved[field.id]), value)
    ) {
      conflicts[field.id] = saved[field.id] ?? null;
    }
  }

  const unavailable = snapshot.definitions.some((field) => {
    const latest = currentDefinitions.get(String(field.id));
    return !latest || (field.editable && !latest.editable);
  });
  const notice = [
    "Latest fields and values loaded. Review the form before saving again.",
    unavailable && "Some fields are no longer available for editing.",
    !unavailable &&
      JSON.stringify(snapshot.definitions) !==
        JSON.stringify(current.definitions) &&
      "Field settings changed.",
    removedChoices && "Unavailable choices were removed from your draft.",
    changedType &&
      "Some fields changed type; their latest saved values are shown.",
  ]
    .filter(Boolean)
    .join(" ");
  return { draft, conflicts, notice };
};

export default recoverAdditionalDetailsDraft;
